import React, { useState } from "react";
import { UserPlus, Send, ShieldAlert, Check } from "lucide-react";
import { Button } from "../shared/Button";
import { visitas, restricciones as apiRestricciones } from "../../api/modulos.service";
import { estilos as est, Entrada, Seleccion, Area, Aviso, Ventana } from "./base";
import CapturaFotos from "./CapturaFotos";

/**
 * ALGUIEN LLEGÓ SIN AVISAR.
 *
 * Reemplaza la llamada de la caseta a la casa, que es de donde sale la mitad
 * de la fricción de una residencial. El guardia llena lo que ve, el residente
 * recibe dos botones en el teléfono, y el guardia se entera de la respuesta
 * sin colgar nada.
 *
 * **Se comprueban las restricciones mientras se escribe el nombre**, no al
 * enviar: si la persona está vetada, el guardia tiene que saberlo antes de
 * terminar de llenar el formulario, no después.
 */
export default function LlegadaSinAviso({ catalogos, puntosAcceso = [], alRegistrar }) {
  const [form, setForm] = useState({
    visitorName: "",
    houseNumber: "",
    polygon: "",
    personSought: "",
    visitorType: "invitado",
    reason: "",
    visitorPhone: "",
    arrivalMode: "foot",
    plate: "",
    vehicleBrand: "",
    vehicleColor: "",
    accessPoint: "",
  });
  const [alerta, setAlerta] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [creada, setCreada] = useState(null);
  const [error, setError] = useState("");

  const cambiar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  /* La comprobación se dispara al salir del campo y no en cada tecla: una
     consulta por pulsación llenaría el servidor de ruido sin decir nada
     nuevo hasta que el nombre está completo. */
  const comprobar = async () => {
    if (!form.visitorName && !form.plate) return;
    try {
      const r = await apiRestricciones.comprobar({
        nombre: form.visitorName || undefined,
        placa: form.plate || undefined,
      });
      setAlerta(r.hayRestriccion ? r : null);
    } catch {
      // Si la comprobación falla, el registro sigue. Dejar al guardia sin
      // poder registrar a nadie porque una consulta falló sería peor.
      setAlerta(null);
    }
  };

  const enviar = async (e) => {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      const r = await visitas.solicitarAutorizacion({
        ...form,
        plate: form.arrivalMode === "foot" ? "" : form.plate,
        accessPoint: form.accessPoint || undefined,
      });
      setCreada(r.visit);
      if (alRegistrar) alRegistrar(r.visit);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo pedir la autorización");
    } finally {
      setEnviando(false);
    }
  };

  if (creada) {
    return (
      <div style={est.seccion}>
        <div style={{ ...est.tarjeta, alignItems: "center", textAlign: "center", gap: "var(--space-4)" }}>
          <Send size={32} style={{ color: "var(--gold)" }} />
          <h3 style={est.tarjetaTitulo}>Se avisó al residente</h3>
          <p style={est.subtitulo}>
            {creada.visitorName} quedó en espera de autorización. En cuanto el residente conteste,
            lo verá en la lista del día.
          </p>
          <CapturaFotos
            ambito="visitas"
            recursoId={creada._id}
            alGuardar={(fotos) => visitas.adjuntarFotos(creada._id, { photos: fotos })}
          />
          <Button onClick={() => { setCreada(null); setForm({ ...form, visitorName: "", plate: "" }); }}>
            Registrar a otra persona
          </Button>
        </div>
      </div>
    );
  }

  return (
    /* `minWidth: 0` no es adorno: sin él la pantalla se desborda en el
       teléfono. Esta pantalla se monta como celda de una rejilla, y una celda
       de rejilla arranca con `min-width: auto`, o sea que se niega a medir
       menos que su contenido más rígido. Medido a 390 px: el botón de abajo
       pedía 445 px de ancho mínimo, la celda disponible eran 366, y la página
       terminaba con 67 px de barra horizontal. De paso, esos 445 px hacían que
       cada `est.fila` creyera tener sitio para dos columnas. */
    <form style={{ ...est.seccion, minWidth: 0 }} onSubmit={enviar}>
      <div style={est.cabecera}>
        <div>
          <h2 style={est.titulo}>Llegó alguien sin aviso</h2>
          <p style={est.subtitulo}>
            Se le pregunta al residente desde aquí. No hace falta llamar.
          </p>
        </div>
      </div>

      {alerta && (
        <Aviso>
          <strong>Atención.</strong>{" "}
          {alerta.bloquea
            ? "Esta persona o este vehículo tienen ACCESO BLOQUEADO."
            : "Hay una restricción registrada."}{" "}
          {alerta.hallazgos.map((h) => h.reason).join(" · ")}
        </Aviso>
      )}

      {error && <Aviso>{error}</Aviso>}

      <div style={est.fila}>
        <Entrada
          etiqueta="Nombre del visitante"
          value={form.visitorName}
          onChange={cambiar("visitorName")}
          onBlur={comprobar}
          placeholder="Nombre y apellido"
          required
        />
        <Seleccion
          etiqueta="Tipo de visitante"
          value={form.visitorType}
          onChange={cambiar("visitorType")}
          opciones={catalogos?.tiposVisitante || []}
        />
      </div>

      <div style={est.fila}>
        <Entrada etiqueta="Polígono" value={form.polygon} onChange={cambiar("polygon")} required />
        <Entrada etiqueta="Casa" value={form.houseNumber} onChange={cambiar("houseNumber")} required />
      </div>

      <div style={est.fila}>
        <Entrada
          etiqueta="¿A quién busca?"
          value={form.personSought}
          onChange={cambiar("personSought")}
          placeholder="Opcional"
        />
        <Entrada
          etiqueta="Teléfono"
          value={form.visitorPhone}
          onChange={cambiar("visitorPhone")}
          placeholder="7000-0000"
        />
      </div>

      <Area
        etiqueta="Motivo"
        value={form.reason}
        onChange={cambiar("reason")}
        placeholder="Qué viene a hacer"
      />

      <div style={est.fila}>
        <Seleccion
          etiqueta="Cómo llega"
          value={form.arrivalMode}
          onChange={cambiar("arrivalMode")}
          opciones={[
            { valor: "foot", etiqueta: "A pie" },
            { valor: "vehicle", etiqueta: "En vehículo" },
            { valor: "motorcycle", etiqueta: "En moto" },
          ]}
        />
        {puntosAcceso.length > 0 && (
          <Seleccion
            etiqueta="Punto de acceso"
            value={form.accessPoint}
            onChange={cambiar("accessPoint")}
            opciones={[{ valor: "", etiqueta: "Sin especificar" },
              ...puntosAcceso.map((p) => ({ valor: p._id, etiqueta: p.name }))]}
          />
        )}
      </div>

      {form.arrivalMode !== "foot" && (
        <div style={est.fila}>
          <Entrada
            etiqueta="Placa"
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
            onBlur={comprobar}
            placeholder="P123-456"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            required
          />
          <Entrada etiqueta="Marca" value={form.vehicleBrand} onChange={cambiar("vehicleBrand")} />
          <Entrada etiqueta="Color" value={form.vehicleColor} onChange={cambiar("vehicleColor")} />
        </div>
      )}

      {/* `whiteSpace: "normal"` porque `shared/Button` trae `nowrap` y
          `overflow: hidden`. Con la mayúscula y el espaciado de letras del
          sistema, «PEDIR AUTORIZACIÓN AL RESIDENTE» en una sola línea necesita
          un botón de 445 px y en el teléfono hay 366: el texto se cortaba
          dentro del botón — justo el botón que el guardia tiene que reconocer
          con alguien esperando. Partido en dos líneas cabe de sobra. */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        leftIcon={UserPlus}
        loading={enviando}
        fullWidth
        style={{ whiteSpace: "normal" }}
      >
        Pedir autorización al residente
      </Button>
    </form>
  );
}
